import { useState, useEffect, useRef, useCallback } from "react";

import {
  TableConfig,
  SavedTableConfig,
  DEFAULT_TABLE_CONFIG,
  genActionId,
} from "../types/table";

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function fetchConfigs(): Promise<SavedTableConfig[]> {
  try {
    const res = await fetch("/api/table-configs");
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data)) return data;
    }
  } catch {
    // API unavailable or non-JSON response — fall through to localStorage
  }
  try {
    return JSON.parse(localStorage.getItem("slateTable_configs") ?? "[]");
  } catch {
    return [];
  }
}

async function persistConfigs(configs: SavedTableConfig[]) {
  try {
    const res = await fetch("/api/table-configs", {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(configs),
    });
    const contentType = res.headers.get("content-type") ?? "";
    if (res.ok && contentType.includes("application/json")) return;
  } catch {
    // API unavailable — fall through to localStorage
  }
  localStorage.setItem("slateTable_configs", JSON.stringify(configs));
}

/**
 * Migrate legacy configs that used a plain `rowActionsText` string
 * instead of the current `rowActionItems` array.
 */
function migrateConfig(cfg: SavedTableConfig): SavedTableConfig {
  const legacy = cfg as SavedTableConfig & { rowActionsText?: string };
  if (
    legacy.rowActionsText &&
    (!cfg.rowActionItems || cfg.rowActionItems.length === 0)
  ) {
    const labels = legacy.rowActionsText
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    return {
      ...cfg,
      rowActionItems: labels.map((label) => ({
        id: genActionId(),
        label,
        display: "overflow" as const,
        variant: "secondary" as const,
      })),
    };
  }
  return cfg;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTableConfig(
  tableId?: string,
  displayName?: string,
  initialConfig?: TableConfig
) {
  const [config, setConfig] = useState<TableConfig>(
    initialConfig ?? DEFAULT_TABLE_CONFIG
  );
  const [configName, setConfigName] = useState(
    displayName ?? tableId ?? "Untitled table"
  );
  const [savedConfigs, setSavedConfigs] = useState<SavedTableConfig[]>([]);
  const [configLoaded, setConfigLoaded] = useState(false);
  const autoSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasInitialized = useRef(false);

  // Load saved configs on mount; if tableId provided, auto-load matching config
  useEffect(() => {
    let cancelled = false;

    fetchConfigs().then((raw) => {
      if (cancelled) return;

      const configs = raw.map(migrateConfig);
      setSavedConfigs(configs);

      if (tableId) {
        // Look up by tableId field first, then fall back to name match
        const match =
          configs.find((c) => c.tableId === tableId) ??
          configs.find((c) => c.name === tableId);
        if (match) {
          applyConfig(match);
        } else {
          // First time — use initialConfig if provided, otherwise keep default
          if (initialConfig) {
            setConfig(initialConfig);
          }
          setConfigName(displayName ?? tableId);
        }
      }

      hasInitialized.current = true;
      setConfigLoaded(true);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableId]);

  // Auto-save on config/name changes when tableId is provided (debounced)
  useEffect(() => {
    if (!tableId || !hasInitialized.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      saveConfigInternal();
    }, 800);
    return () => {
      if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, configName]);

  // ── Actions ───────────────────────────────────────────────────────────────

  function applyConfig(cfg: SavedTableConfig) {
    setConfig({
      columns: cfg.columns,
      cellValues: cfg.cellValues,
      imageUrlValues: cfg.imageUrlValues,
      subtextValues: cfg.subtextValues,
      rowCount: cfg.rowCount ?? 12,
      rowsSelectable: cfg.rowsSelectable,
      rowActionsEnabled: cfg.rowActionsEnabled,
      rowActionItems: cfg.rowActionItems,
      columnPrompts: cfg.columnPrompts ?? {},
    });
    setConfigName(cfg.name);
  }

  function loadConfig(cfg: SavedTableConfig) {
    // Apply the config data but preserve the table's name when bound to a tableId,
    // so the auto-save doesn't overwrite the original entry's name
    setConfig({
      columns: cfg.columns,
      cellValues: cfg.cellValues,
      imageUrlValues: cfg.imageUrlValues,
      subtextValues: cfg.subtextValues,
      rowCount: cfg.rowCount ?? 12,
      rowsSelectable: cfg.rowsSelectable,
      rowActionsEnabled: cfg.rowActionsEnabled,
      rowActionItems: cfg.rowActionItems,
      columnPrompts: cfg.columnPrompts ?? {},
    });
    if (!tableId) {
      setConfigName(cfg.name);
    }
  }

  function saveConfigInternal() {
    const name = configName.trim() || "Untitled table";
    setSavedConfigs((prev) => {
      // When tableId is set, always upsert by tableId (stable key)
      // Otherwise upsert by name (manual save behavior)
      const existingIdx = tableId
        ? prev.findIndex((c) => c.tableId === tableId)
        : prev.findIndex((c) => c.name === name);
      const entry: SavedTableConfig = {
        id: existingIdx >= 0 ? prev[existingIdx].id : crypto.randomUUID(),
        name,
        savedAt: new Date().toISOString(),
        tableId: tableId ?? undefined,
        ...config,
      };
      const next =
        existingIdx >= 0
          ? prev.map((c, i) => (i === existingIdx ? entry : c))
          : [entry, ...prev];
      persistConfigs(next);
      return next;
    });
  }

  // Public save — same as internal but can be called explicitly
  const saveConfig = useCallback(() => {
    saveConfigInternal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, configName, tableId]);

  function deleteConfig(id: string) {
    setSavedConfigs((prev) => {
      const next = prev.filter((c) => c.id !== id);
      persistConfigs(next);
      return next;
    });
  }

  function updateConfig(patch: Partial<TableConfig>) {
    setConfig((prev) => ({ ...prev, ...patch }));
  }

  return {
    config,
    setConfig,
    configName,
    setConfigName,
    savedConfigs,
    configLoaded,
    saveConfig,
    loadConfig,
    deleteConfig,
    updateConfig,
  };
}
